import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDidUpdate } from '@lt/components/hooks';
import type { CLIENT } from '@lt/api/dist/types/client'; // todo почему не экспорятся сущности из @lt/api ?
import { api } from 'src/api';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { css } from 'styled-components';
import { useSocialAuth } from 'src/components/AuthNew/hooks/useSocialAuth';
import {
  isSignInByPhone,
  isSignInProcess,
  isSignupByPhone,
  isSignUpByAuthProvider,
  isSignupProcess,
  mapSharedStateToClient,
  resetPhoneFormat,
  removeURLParam,
  removeRedirectParams,
  setPhoneFormat,
} from 'src/components/AuthNew/utils';
import { mapEmailAuthErrorCodeToMessage } from 'src/components/AuthNew/errors';
import { useSearchParams } from 'src/components/AuthNew/hooks/useResetPasswordToken';
import {
  getClientUnconfirmedPhone,
  getClientId,
  getShowLogOutPopup,
} from 'src/store/client/selectors';
import { clientStorage } from 'src/api/storage';
import {
  useAuth,
  useConfirmPhone,
  useCreatePassword,
  useGetNewCode,
  useLogin,
  useRegistration,
} from './hooks';
import type {
  AuthLayoutCSS,
  CustomAuthComponents,
  SharedState,
  ConfirmPhoneConfig,
  QueryParams,
} from './types';
import {
  AuthFormType,
  AuthProvider,
  RequestStatus,
  PageType,
  ClientType,
  ConfirmedData,
  TokenType,
} from './types';
import { LoginForm } from './components/LoginForm';
import { SmsCodeForm } from './components/SmsCodeForm';
import { SendSmsCodeAgainForm } from './components/SendSmsCodeAgainForm';
import { PasswordForm } from './components/PasswordForm';
import { RegistrationForm } from './components/RegistrationForm';
import { MailConfirmationForm } from './components/MailConfirmationForm';
import { PasswordCreateForm } from './components/PasswordCreateForm';
import { ResetPasswordForm } from './components/ResetPasswordForm';
import { ResendEmailConfirmationForm } from './components/ResendEmailConfirmationForm';
import { getAuthLayoutWithCSS } from './components/AuthLayout';
import { ConfirmPhoneForm } from './components/ConfirmPhoneForm';
import { Fail } from './components/Fail';
import { clientTypeScope } from './utils/clientTypeScope';
import { EmailOrPasswordConfirmationForm } from './components/EmailOrPasswordConfirmationForm';
import { InvalidTokenForm } from './components/InvalidTokenForm';
import { ConfirmationProcessForm } from './components/ConfirmationProcessForm';
import { LogOutForm } from './components/LogOutForm';
import { RestorePasswordAfterLeakForm } from './components/RestorePasswordAfterLeakForm';
import { pushAuthOpen, pushAuthPageView } from './analytics';
import ReCaptchaModal from '../ReCaptchaModal';

type Props = {
  isMobile?: boolean;
  isWhiteLabel?: boolean;
  onSignIn?: (client: CLIENT) => void;
  onSignUp?: (client: CLIENT) => void;
  onLogOut?: (shouldShowAuthPopup?: boolean) => void;
  setConfirmedPhone?: (confirmPhoneConfig: ConfirmPhoneConfig) => void;
  /** @desc колек для сохранения пользователя без авторизации, */
  onSaveClient?: (client: CLIENT) => void;
  showCloseButton?: boolean;
  authLayoutCSS?: AuthLayoutCSS;
  onClose: (client?: CLIENT) => void;
  pageType?: PageType;
  onSuccessfulSignUpByPhone?: (sharedState?: SharedState) => void;
  initialFormType?: AuthFormType;
  clientType?: ClientType;
  outsideSharedState?: SharedState;
} & CustomAuthComponents;

const initialSharedState: SharedState = {
  phone: '',
  phone_mask: '',
  country: '',
  email: '',
  password: '',
  name: '',
  surname: '',
  signUpType: null,
};

const initialQueryParams: QueryParams = {
  inviter_id: undefined,
};

const AuthNew = ({
  isMobile,
  isWhiteLabel,
  onSignIn = () => null,
  onSignUp = () => null,
  onLogOut = () => null,
  onSaveClient = () => null,
  setConfirmedPhone = () => null,
  authLayoutCSS,
  showCloseButton,
  onClose,
  CustomMailConfirmationForm,
  pageType = PageType.Default,
  onSuccessfulSignUpByPhone = () => null,
  initialFormType = AuthFormType.Login,
  clientType = ClientType.Unauthorized,
  outsideSharedState,
}: Props) => {
  const [requestStatus, setRequestStatus] = useState(RequestStatus.Idle);
  const [isMailConfirmProccessed, setMailConfirmProccessed] = useState(false);

  const [sharedState, setSharedState] =
    useState<SharedState>(initialSharedState);
  const [queryParams, setQueryParams] =
    useState<QueryParams>(initialQueryParams);

  useEffect(() => {
    if (outsideSharedState?.email)
      setSharedState({
        ...initialSharedState,
        email: outsideSharedState.email,
      });
    else if (outsideSharedState?.phone)
      setSharedState({
        ...initialSharedState,
        phone: outsideSharedState.phone,
      });
  }, [outsideSharedState]);

  const [formType, setFormType] = useState(initialFormType);
  const [isShownCloseIcon, setIsShownCloseIcon] = useState(showCloseButton);
  const [isShownBackIcon, setIsShownBackIcon] = useState(true);

  const { reset_password_token } = useSearchParams('reset_password_token');
  const { confirmation_token } = useSearchParams('confirmation_token');
  const { show_login_form } = useSearchParams('show_login_form');
  const { redirect_to } = useSearchParams('redirect_to');
  // через деструктуризацию возникают конфликты с другими переменными
  const clientEmail = useSearchParams('email').email;
  const { inviter_id } = useSearchParams('inviter_id');

  // Нужная инфа о клиенте
  const unconfirmedPhone = useSelector(getClientUnconfirmedPhone);
  const clientId = useSelector(getClientId);
  const isShownLogOutForm = useSelector(getShowLogOutPopup);

  useEffect(() => {
    if (confirmation_token || reset_password_token)
      setFormType(AuthFormType.ConfirmProcess);
    else if (reset_password_token) {
      setFormType(AuthFormType.CreatePassword);
    }
    if (show_login_form) setFormType(AuthFormType.Login);
    setQueryParams({ ...queryParams, inviter_id });
    if (isShownLogOutForm) {
      const closeLogOutPopupTimer = setTimeout(() => {
        onClose();
        // поскольку страница заказа - легаси, то необходимо обновлять страницу,
        // чтобы подтянулись/обновились некоторые данные.
        // TODO: убрать после переноса страницы заказа на React
        if (pageType === PageType.Order) window.location.reload();
      }, 5000);

      return () => {
        clearTimeout(closeLogOutPopupTimer);
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reset_password_token,
    confirmation_token,
    show_login_form,
    isShownLogOutForm,
  ]);

  useEffect(() => {
    setFormType(initialFormType);
  }, [initialFormType]);

  useEffect(() => {
    pushAuthOpen();
  }, []);

  useEffect(() => {
    if (
      clientType === ClientType.ConfEmailUnconfPhone &&
      pageType === PageType.Testimonials
    ) {
      setSharedState({
        ...initialSharedState,
        unconfirmed_phone: unconfirmedPhone || undefined,
        id: clientId || undefined,
      });
      setFormType(AuthFormType.SetPhoneForConfirm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clientTypeScope(
      clientType,
      formType,
      pageType,
      setIsShownBackIcon,
      setIsShownCloseIcon,
    );
  }, [clientType, formType, pageType]);

  const resetSharedState = useCallback(
    () => setSharedState(initialSharedState),
    [],
  );

  const handleConfirmedMail = useCallback(
    (confirmedEmail: string) => {
      removeURLParam('confirmation_token');
      if (reset_password_token) setFormType(AuthFormType.CreatePassword);
      else if (isMobile) onClose();
      else {
        setSharedState({ ...sharedState, email: confirmedEmail });
        setFormType(AuthFormType.ChangedMailConfirmation);
      }
      setMailConfirmProccessed(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reset_password_token],
  );

  const {
    timeLeftToDeliver,
    handleGetNewCode,
    timer,
    isOpenReCaptcha: isOpenReCaptchaNewCode,
    captchaCallback: captchaCallbackNewCode,
  } = useGetNewCode();

  const { handleConfirmPhone, handleOtpError } = useConfirmPhone({
    onSuccessConfirmPhone: () => {
      if (!sharedState) {
        // eslint-disable-next-line no-console
        console.log('sharedState is not define', sharedState);
        return;
      }

      if (isSignupProcess(sharedState)) {
        setFormType(AuthFormType.MailConfirmation);
        onSuccessfulSignUpByPhone(sharedState);
        if (sharedState.unconfirmed_phone) {
          setConfirmedPhone({
            confirmedPhone: sharedState.unconfirmed_phone,
            shouldHideAuthPopup: false,
          });
        }
      } else if (sharedState.unconfirmed_phone) {
        setConfirmedPhone({
          confirmedPhone: sharedState.unconfirmed_phone,
          shouldHideAuthPopup: true,
        });
      }
    },
    setRequestStatus,
    onSendSmsCodeAgain: () => setFormType(AuthFormType.SendSmsCodeAgain),
  });

  useDidUpdate(() => {
    setRequestStatus(RequestStatus.Idle);
  }, [formType]);

  const handleAuthErrors = (error: AxiosError) => {
    if (isSignupByPhone(sharedState) || isSignInByPhone(sharedState)) {
      handleOtpError(error);
    } else {
      throw new Error(mapEmailAuthErrorCodeToMessage(error));
    }
  };

  const {
    handleLogin,
    isOpenReCaptcha: isOpenReCaptchaLogin,
    captchaCallback: captchaCallbackLogin,
  } = useLogin({
    setFormType,
    handleGetNewCode,
    setSharedState,
    handleRestorePassword,
    handleResendEmailConfirmation,
  });

  const { handleAuth } = useAuth({
    setAuthRequestStatus: setRequestStatus,
    onSuccess: (client) => {
      if (isSignupProcess(sharedState)) {
        setFormType(AuthFormType.MailConfirmation);
        setSharedState((state) => ({
          ...state,
          email: client.unconfirmed_email || '',
        }));
        onSignUp(client);
      }

      // Used for browser cookie authentication
      else
        axios.get(client.extra.auth_url).then((res) => {
          if (res.data.success) {
            onSignIn(client);
            redirectTo();
          } else setFormType(AuthFormType.Failure);
        });
    },
    onError: handleAuthErrors,
    setSharedState,
    setFormType,
  });

  const { loginSocialNetwork } = useSocialAuth({
    onSuccess: (client) => {
      setRequestStatus(RequestStatus.Succeeded);

      // Used for browser cookie authentication
      axios.get(client.extra.auth_url).then((res) => {
        if (res.data.success) {
          onSignIn(client);
        } else setFormType(AuthFormType.Failure);
      });
    },
    setRequestStatus,
    setFormType,
    setSharedState,
    handleGetNewCode,
  });

  const shouldSkipPhoneConfirmation =
    sharedState.signUpType === AuthProvider.TBank;

  const {
    handleRegistration,
    isOpenReCaptcha: isOpenReCaptchaRegistration,
    captchaCallback: captchaCallbackRegistration,
  } = useRegistration({
    setSharedState,
    setRequestStatus,
    setFormType,
    handleGetNewCode,
    onSignUp: (client) => {
      if (shouldSkipPhoneConfirmation) {
        onSignUp(client);
      } else {
        onSignUp(client);
        onSaveClient(client);
      }
    },
    shouldSkipPhoneConfirmation,
  });

  const { handleCreatePassword } = useCreatePassword({
    setRequestStatus,
    onSuccess: (confirmedEmail: string) => {
      setSharedState({ ...sharedState, email: confirmedEmail });
      setFormType(AuthFormType.PasswordConfirmation);
    },
    onFail: () => {
      setFormType(AuthFormType.InvalidToken);
    },
  });
  const handleResetPassword = async (email: string): Promise<void> => {
    try {
      const response = await api.postClientRestorePassword({ email });
      const { success } = response;

      if (success) {
        setRequestStatus(RequestStatus.Succeeded);
        setSharedState({ ...sharedState, email });
        setFormType(AuthFormType.ResetPassword);
      } else {
        setRequestStatus(RequestStatus.Failed);
        setFormType(AuthFormType.Login);
      }
    } catch (error) {
      setRequestStatus(RequestStatus.Failed);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  async function handleResendEmailConfirmation(
    email: string | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    try {
      if (!email) return;

      const response = await api.getClientResendEmailConfirmation({ email });
      const { success } = response;

      if (success) {
        setRequestStatus(RequestStatus.Succeeded);
        setSharedState({ ...sharedState, email });
        setFormType(AuthFormType.ResendEmailConfirmationForm);
      } else {
        setRequestStatus(RequestStatus.Failed);
        setFormType(AuthFormType.Login);
      }
    } catch (error) {
      setRequestStatus(RequestStatus.Failed);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  async function handleRestorePassword(
    email: string | undefined,
    hasPasswordLeaked: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    try {
      if (!email) return;

      const response = await api.postClientRestorePassword({ email });
      const { success } = response;

      if (success) {
        setRequestStatus(RequestStatus.Succeeded);
        setSharedState({ ...sharedState, email });
        if (hasPasswordLeaked)
          setFormType(AuthFormType.RestorePasswordAfterLeak);
        else setFormType(AuthFormType.RestorePassword);
      } else {
        setRequestStatus(RequestStatus.Failed);
        setFormType(AuthFormType.Login);
      }
    } catch (error) {
      setRequestStatus(RequestStatus.Failed);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  const handleLogOut = async (shouldShowAuthPopup?: boolean) => {
    onLogOut(shouldShowAuthPopup);
    // Used for browser cookie deauthentication
    const authToken = clientStorage.getAuthToken();
    await axios.get(`${window.location.origin}/client/logout`, {
      params: {
        auth_token: authToken,
      },
    });
  };

  // Функция редиректа, если в урле есть параметр redirect_to
  function redirectTo() {
    if (redirect_to && redirect_to !== '/')
      window.location.href = window.location.origin + redirect_to;
    else removeRedirectParams(redirect_to, show_login_form);
  }

  const backToLoginForm = (value = '') => {
    resetSharedState();
    if (typeof value === 'string') {
      if (/[a-zA-Zа-яА-яёЁ]/.test(value)) {
        setSharedState({ ...initialSharedState, email: value });
      } else {
        setSharedState({ ...initialSharedState, phone: value });
      }
    }
    setFormType(AuthFormType.Login);
    if (isShownCloseIcon) handleLogOut(true);
  };

  // если страница чекаута, то не передаем onClose и не отображаем крестик закрытия попапа
  const handleCloseClick =
    pageType === PageType.Checkout ? undefined : onClose || backToLoginForm;

  const handleClose = (client?: CLIENT) => {
    removeRedirectParams(redirect_to, show_login_form);
    removeURLParam('reset_password_token');
    removeURLParam('confirmation_token');
    removeURLParam('email');
    if (handleCloseClick) handleCloseClick(client);
  };

  const backToConfirmPhoneForm = () => {
    setIsShownCloseIcon(false);
    setFormType(AuthFormType.SetPhoneForConfirm);
  };

  const handleCloseConfirmation = () => {
    removeURLParam('reset_password_token');
    removeURLParam('confirmation_token');
    removeURLParam('email');
    onClose();
  };

  const handleCloseSuccessPassword = () => {
    if (isMobile) onClose();
    else setFormType(AuthFormType.Login);
  };

  const infoPopupWrapperCSS = [
    css`
      height: 100%;
      justify-content: center;
    `,
  ];

  const resetPasswordFormCSS = [
    css`
      width: 100%;
    `,
  ];

  const AuthLayoutWithCSS = useMemo(
    () =>
      getAuthLayoutWithCSS(authLayoutCSS)(
        formType,
        isShownCloseIcon,
        isShownBackIcon,
        isMobile,
      ),
    [authLayoutCSS, formType, isShownCloseIcon, isShownBackIcon, isMobile],
  );

  const handleCaptchaPassed = () => {
    if (isOpenReCaptchaNewCode && captchaCallbackNewCode) {
      captchaCallbackNewCode();
    }
    if (isOpenReCaptchaRegistration && captchaCallbackRegistration) {
      captchaCallbackRegistration();
    }
    if (isOpenReCaptchaLogin && captchaCallbackLogin) {
      captchaCallbackLogin();
    }
  };

  const isOpenReCaptcha =
    isOpenReCaptchaNewCode ||
    isOpenReCaptchaRegistration ||
    isOpenReCaptchaLogin;

  const ReCaptcha = (
    <ReCaptchaModal
      isOpened={isOpenReCaptcha}
      onCaptchaPassed={handleCaptchaPassed}
    />
  );

  useEffect(() => {
    pushAuthPageView(formType, isMobile || false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType]);

  switch (formType) {
    case AuthFormType.Login:
      return (
        <AuthLayoutWithCSS onClose={handleClose}>
          <LoginForm
            isWhiteLabel={isWhiteLabel}
            setSharedState={setSharedState}
            onSocialNetworkSubmit={loginSocialNetwork}
            onSubmit={handleLogin}
            requestStatus={requestStatus}
            sharedState={sharedState}
            {...(authLayoutCSS && authLayoutCSS[AuthFormType.Login])}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.LoginWithPassword:
      return (
        <AuthLayoutWithCSS onClose={handleClose} onBack={backToLoginForm}>
          <PasswordForm
            requestStatus={requestStatus}
            onSubmit={handleAuth}
            email={sharedState.email || sharedState.unconfirmed_email || ''}
            onResetPassword={handleResetPassword}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.LoginWithSMSCode:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick} onBack={backToLoginForm}>
          <SmsCodeForm
            timeLeftToDeliver={timeLeftToDeliver}
            timer={timer}
            clientId={sharedState?.id}
            onGetNewCode={handleGetNewCode}
            requestStatus={requestStatus}
            switchForm={setFormType}
            onAuth={handleAuth}
            phone={setPhoneFormat(
              sharedState.phone_mask ?? '',
              sharedState.phone ?? '',
            )}
            isConfirmPhoneProcess={false}
            isAuthProcess
            onConfirmPhone={handleConfirmPhone}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.SendSmsCodeAgain:
      return (
        <AuthLayoutWithCSS>
          <SendSmsCodeAgainForm
            backToStartHandler={() => {
              setFormType(AuthFormType.Login);
            }}
            getNewCodeOnSuccess={() => {
              setFormType(AuthFormType.LoginWithSMSCode);
            }}
            onSubmit={handleGetNewCode}
            phone={resetPhoneFormat(
              sharedState.phone || sharedState.unconfirmed_phone,
            )}
            isAuthProcess={isSignInProcess(sharedState)}
            clientId={sharedState?.id}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.Registration:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick} onBack={backToLoginForm}>
          <RegistrationForm
            isMobile={isMobile}
            requestStatus={requestStatus}
            sharedState={sharedState}
            setSharedState={setSharedState}
            name={sharedState.name}
            surname={sharedState.surname}
            email={sharedState.email}
            phone={setPhoneFormat(
              sharedState.phone_mask ?? '',
              sharedState.phone,
            )}
            id={sharedState.id}
            queryParams={queryParams}
            onSubmit={handleRegistration}
            backToLoginForm={backToLoginForm}
            {...(authLayoutCSS && authLayoutCSS[AuthFormType.Registration])}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.MailConfirmation:
      return (
        <AuthLayoutWithCSS
          onClose={() =>
            handleCloseClick &&
            handleCloseClick(mapSharedStateToClient(sharedState))
          }
        >
          {CustomMailConfirmationForm ? (
            <CustomMailConfirmationForm
              email={sharedState.email}
              emailIsConfirmed={isSignUpByAuthProvider(sharedState)}
              onClose={() =>
                handleCloseClick &&
                handleCloseClick(mapSharedStateToClient(sharedState))
              }
            />
          ) : (
            <MailConfirmationForm
              email={sharedState.email}
              emailIsConfirmed={isSignUpByAuthProvider(sharedState)}
              onClose={onClose}
            />
          )}

          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.SetPhoneForConfirm:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick}>
          <ConfirmPhoneForm
            handleGetNewCode={handleGetNewCode}
            timeLeftToDeliver={timeLeftToDeliver}
            setSharedState={setSharedState}
            setFormType={setFormType}
            setRequestStatus={setRequestStatus}
            sharedState={sharedState}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.ConfirmPhoneWithSMSCode:
      return (
        <AuthLayoutWithCSS onBack={backToConfirmPhoneForm}>
          <SmsCodeForm
            timeLeftToDeliver={timeLeftToDeliver}
            timer={timer}
            clientId={sharedState?.id}
            onGetNewCode={handleGetNewCode}
            requestStatus={requestStatus}
            switchForm={setFormType}
            onAuth={handleAuth}
            phone={setPhoneFormat(
              sharedState.phone_mask ?? '',
              sharedState.unconfirmed_phone ?? '',
            )}
            isConfirmPhoneProcess
            isAuthProcess={false}
            onConfirmPhone={handleConfirmPhone}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.ConfirmAndLoginPhoneWithSMSCode:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick} onBack={backToLoginForm}>
          <SmsCodeForm
            timeLeftToDeliver={timeLeftToDeliver}
            timer={timer}
            clientId={sharedState?.id}
            onGetNewCode={handleGetNewCode}
            requestStatus={requestStatus}
            switchForm={setFormType}
            onAuth={handleAuth}
            phone={setPhoneFormat(
              sharedState.phone_mask ?? '',
              sharedState.unconfirmed_phone ?? '',
            )}
            isConfirmPhoneProcess
            isAuthProcess
            onConfirmPhone={handleConfirmPhone}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.CreatePassword:
      return (
        <AuthLayoutWithCSS onClose={handleClose}>
          <PasswordCreateForm
            isMailConfirmProccessed={isMailConfirmProccessed}
            requestStatus={requestStatus}
            onSubmit={handleCreatePassword}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.ResendEmailConfirmationForm:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick}>
          <ResendEmailConfirmationForm email={sharedState.email} />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.ResetPassword:
      return (
        <AuthLayoutWithCSS
          onClose={handleCloseClick}
          isShownBackIcon
          wrapperCSS={resetPasswordFormCSS}
          onBack={backToLoginForm}
        >
          <ResetPasswordForm
            email={sharedState.email}
            handleGoBack={backToLoginForm}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.PasswordConfirmation:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick}>
          <EmailOrPasswordConfirmationForm
            onClose={handleCloseClick}
            isLoggedIn={!!clientId}
            confirmedData={ConfirmedData.Password}
            backToLoginForm={handleCloseSuccessPassword}
            isMobile={isMobile}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.ChangedMailConfirmation:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick}>
          <EmailOrPasswordConfirmationForm
            onClose={handleCloseClick}
            isLoggedIn={!!clientId}
            confirmedData={ConfirmedData.Email}
            backToLoginForm={handleCloseSuccessPassword}
            isMobile={isMobile}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.RestorePassword:
      return (
        <AuthLayoutWithCSS
          onClose={handleCloseClick}
          isShownBackIcon
          wrapperCSS={resetPasswordFormCSS}
          onBack={backToLoginForm}
        >
          <ResetPasswordForm
            email={sharedState.email}
            handleGoBack={backToLoginForm}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.RestorePasswordAfterLeak:
      return (
        <AuthLayoutWithCSS onClose={handleCloseClick}>
          <RestorePasswordAfterLeakForm email={sharedState.email} />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.SuccessLogin:
      onClose();
      return null;
    case AuthFormType.ConfirmProcess:
      return (
        <AuthLayoutWithCSS onClose={handleCloseConfirmation}>
          <ConfirmationProcessForm
            setFormType={setFormType}
            onSuccess={handleConfirmedMail}
            setSharedState={setSharedState}
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.InvalidToken:
      return (
        <AuthLayoutWithCSS onClose={handleCloseConfirmation}>
          <InvalidTokenForm
            setFormType={setFormType}
            email={clientEmail || sharedState.email}
            tokenType={
              confirmation_token
                ? TokenType.ConfirmationToken
                : TokenType.ResetPasswordToken
            }
          />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    case AuthFormType.LogOut:
      return (
        <AuthLayoutWithCSS onClose={onClose} wrapperCSS={infoPopupWrapperCSS}>
          <LogOutForm />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
    default:
      return (
        <AuthLayoutWithCSS
          onClose={handleCloseClick}
          wrapperCSS={infoPopupWrapperCSS}
        >
          <Fail isMobile={isMobile} onSubmit={backToLoginForm} />
          {ReCaptcha}
        </AuthLayoutWithCSS>
      );
  }
};

export default AuthNew;
